import { useEffect, useCallback } from "react";
import { getStroke } from "perfect-freehand";
import api from "../api/api";

function getSvgPathFromStroke(stroke) {
    if (!stroke.length) return '';
    const d = stroke.reduce(
        (acc, [x0, y0], i, arr) => {
            const [x1, y1] = arr[(i + 1) % arr.length];
            acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
            return acc;
        },
        ['M', ...stroke[0], 'Q']
    );
    d.push('Z');
    return d.join(' ');
}

const STROKE_OPTIONS = { thinning: 0.6, smoothing: 0.5, streamline: 0.5, simulatePressure: true };

// A sticky note that has never been saved and is still open (or still empty).
const isUnsentPdfText = (t) =>
    String(t.id).startsWith('pdf-annot-') && (t.isEditing || !String(t.text || '').trim());

// 💾 Save PDF Annotations (Highlights, Text, Drawings)
const useWorkspaceSaver = (context, enableAutosave = true) => {
    // Context object se props destructure karein via useApp
    const {
        pdfId, activeWorkspace,
        snippets, setSnippets,
        editableBoxes, setEditableBoxes,
        lines, setLines,
        connections, setConnections,
        highlights, setHighlights,
        pdfAnnotations, setPdfAnnotations,
        pdfLines, setPdfLines,
        brushHighlights, setBrushHighlights,
        existingSnippetsMap,
        isDirty, setIsDirty,
        savingWorkspace, setSavingWorkspace,
        savingPdf, setSavingPdf,
        deletedHighlights, setDeletedHighlights,
        deletedPdfTexts, setDeletedPdfTexts,
        autosaveInterval,
        viewStateRef,
        hydratedWorkspaceIdRef,
        hydratedPdfIdRef,
        groups,
        crossPdfLinks,
        setCrossPdfLinks,
    } = context;

    const savePdfChanges = useCallback(async () => {
        if (savingPdf || !pdfId) return;
        // Annotations for this pdfId must be loaded first — the save endpoint
        // deletes every highlight/text/drawing not in the payload, so saving
        // mid-tab-switch would wipe the incoming PDF's annotations (and copy the
        // previous PDF's onto it).
        if (hydratedPdfIdRef?.current !== pdfId) {
            console.warn("⏭️ Skipping PDF save — annotations not loaded yet");
            return;
        }
        setSavingPdf(true);
        console.log("💾 Saving PDF Annotations...");

        try {
            // Prepare payload
            const sentPdfTexts = pdfAnnotations.filter(t => !isUnsentPdfText(t));
            const sentTempIds = new Set(sentPdfTexts.map(t => String(t.id)).filter(id => id.startsWith('pdf-annot-')));
            const payload = {
                highlights: highlights.map(h => ({
                    id: (typeof h.id === 'string' && h.id.startsWith('temp-')) ? null : h.id,
                    page_num: h.pageNum,
                    color: h.color,
                    x_pct: h.xPct,
                    y_pct: h.yPct,
                    width_pct: h.widthPct,
                    height_pct: h.heightPct,
                    content: h.content
                })),
                // New notes still being typed (or left empty) aren't sent yet — they
                // are kept locally and go out on the next save after editing ends.
                pdf_texts: sentPdfTexts.map(t => ({
                    id: (typeof t.id === 'string' && t.id.startsWith('pdf-annot-')) ? null : t.id,
                    page_num: t.pageNum,
                    text: t.text,
                    x_pct: t.xPct,
                    y_pct: t.yPct
                })),
                pdf_drawing_lines: pdfLines.map(l => ({
                    id: (typeof l.id === 'number') ? l.id : null,
                    page_num: l.pageNum,
                    // inputPts = [[x,y,p],...] new format; points = [x,y,...] old flat format
                    points: l.inputPts || l.points || [],
                    color: l.color,
                    stroke_width: l.width
                })),
                brush_highlights: brushHighlights.map(h => ({
                    id: (typeof h.serverId === 'number') ? h.serverId : null,
                    page_num: h.pageNum,
                    path_data: (h.path || []).map(p => ({ xPct: p.xPct, yPct: p.yPct })),
                    color: h.color,
                    brush_width: h.brushWidth
                })),
                deleted_highlights: deletedHighlights,
                deleted_pdf_texts: deletedPdfTexts
            };

            const res = await api.savePdfAnnotations(pdfId, payload);
            if (res.data.status === "success") {
                // Update State with returned IDs (Sync)
                setHighlights(res.data.highlights.map(hl => ({
                    id: hl.id, pageNum: hl.page_num, color: hl.color, xPct: hl.x_pct, yPct: hl.y_pct,
                    widthPct: hl.width_pct, heightPct: hl.height_pct, content: hl.content
                })));
                // Merge against the latest state: a note being edited keeps its local
                // copy (live text + edit mode) so autosave can't reset it mid-typing.
                setPdfAnnotations(prev => {
                    const local = new Map(prev.map(a => [String(a.id), a]));
                    const saved = res.data.pdf_texts.map(t => {
                        const cur = local.get(String(t.id));
                        if (cur?.isEditing) return cur;
                        return {
                            id: t.id, pageNum: t.page_num, text: t.text, xPct: t.x_pct, yPct: t.y_pct,
                            isEditing: false, ...(cur?.color && { color: cur.color })
                        };
                    });
                    // Keep every new note this save didn't include (incl. ones finished mid-request)
                    const kept = prev.filter(a => String(a.id).startsWith('pdf-annot-') && !sentTempIds.has(String(a.id)));
                    return [...saved, ...kept];
                });
                setPdfLines(res.data.pdf_drawing_lines.map(l => {
                    const pts = l.points || [];
                    const isNewFormat = pts.length > 0 && Array.isArray(pts[0]);
                    if (isNewFormat) {
                        const svgPath = getSvgPathFromStroke(
                            getStroke(pts, { ...STROKE_OPTIONS, size: l.stroke_width || 3, simulatePressure: false, last: true })
                        );
                        return { id: l.id, pageNum: l.page_num, tool: "pen", color: l.color, width: l.stroke_width, inputPts: pts, svgPath };
                    }
                    // old flat format [x,y,x,y,...] — render as Line
                    return { id: l.id, pageNum: l.page_num, tool: "pen", color: l.color, width: l.stroke_width, points: pts };
                }));
                setBrushHighlights(res.data.brush_highlights.map(h => ({
                    id: `brush-${h.id}`, serverId: h.id, pageNum: h.page_num, path: h.path_data, color: h.color, brushWidth: h.brush_width
                })));
                setDeletedHighlights([]);
                setDeletedPdfTexts([]);
            }
        } catch (err) {
            console.error("❌ PDF Save Failed:", err);
            alert("Error saving PDF changes.");
        } finally {
            setSavingPdf(false);
        }
    }, [
        pdfId, highlights, pdfAnnotations, pdfLines, brushHighlights, savingPdf,
        deletedHighlights, deletedPdfTexts, setHighlights, setPdfAnnotations,
        setPdfLines, setBrushHighlights, setDeletedHighlights, setDeletedPdfTexts, setSavingPdf,
        hydratedPdfIdRef
    ]);

    // 💾 Save Workspace (Snippets, Boxes, Connections, Lines)
    const saveWorkspaceChanges = useCallback(async () => {
        if (savingWorkspace) return;
        if (!pdfId || !activeWorkspace) return;
        // Never save a workspace whose data hasn't loaded yet. The backend
        // deletes every row not present in the payload, so saving while state
        // is still empty (initial load in flight, or a load that failed) would
        // permanently erase the workspace's strokes, boxes and snippets.
        if (hydratedWorkspaceIdRef?.current !== activeWorkspace.id) {
            console.warn("⏭️ Skipping workspace save — data not loaded yet");
            return;
        }
        setSavingWorkspace(true);
        console.log("💾 Saving Workspace Changes...");

        const fd = new FormData();
        fd.append("pdfId", pdfId);
        fd.append("cross_pdf_links", JSON.stringify(crossPdfLinks || []));

        // Snippets
        snippets.forEach((s, i) => {
            fd.append(`snippets[${i}][id]`, s.id ?? "");
            fd.append(`snippets[${i}][type]`, s.type);
            fd.append(`snippets[${i}][x]`, s.x);
            fd.append(`snippets[${i}][y]`, s.y);
            fd.append(`snippets[${i}][width]`, s.width ?? "");
            fd.append(`snippets[${i}][height]`, s.height ?? "");
            fd.append(`snippets[${i}][source_pdf_id]`, s.pdf_id || pdfId);
            fd.append(`snippets[${i}][page]`, s.pageNum ?? 1);

            if (s.xPct !== undefined) fd.append(`snippets[${i}][xPct]`, s.xPct);
            if (s.yPct !== undefined) fd.append(`snippets[${i}][yPct]`, s.yPct);
            if (s.widthPct !== undefined) fd.append(`snippets[${i}][widthPct]`, s.widthPct);
            if (s.heightPct !== undefined) fd.append(`snippets[${i}][heightPct]`, s.heightPct);

            if (s.type === "text" || s.type === "anchor") {
                fd.append(`snippets[${i}][content]`, s.text || s.content || "");
            }

            if (s.type === "image") {
                if (s.uploadPath) {
                    // Canvas-uploaded image: the file lives in the backend's uploads/ folder.
                    // Sent on every save — the backend resets content to "image" when it's missing.
                    fd.append(`snippets[${i}][content]`, s.uploadPath);
                } else if (s.file) {
                    fd.append(`snippets[${i}][file]`, s.file);
                } else if (s.id && existingSnippetsMap[s.id]?.src) {
                    fd.append(`snippets[${i}][src]`, existingSnippetsMap[s.id].src);
                }
            }
        });

        // Boxes
        editableBoxes.forEach((b, i) => {
            fd.append(`boxes[${i}][id]`, b.id ?? "");
            fd.append(`boxes[${i}][pdf_id]`, pdfId);
            fd.append(`boxes[${i}][x]`, b.x);
            fd.append(`boxes[${i}][y]`, b.y);
            fd.append(`boxes[${i}][width]`, b.width ?? "");
            fd.append(`boxes[${i}][height]`, b.height ?? "");
            fd.append(`boxes[${i}][text]`, b.text ?? "");
        });

        // Lines
        lines.forEach((l, i) => {
            fd.append(`lines[${i}][id]`, l.id ?? "");
            fd.append(`lines[${i}][pdf_id]`, pdfId);
            fd.append(`lines[${i}][points]`, JSON.stringify(l.inputPts || l.points || []));
            fd.append(`lines[${i}][color]`, l.color ?? "black");
            fd.append(`lines[${i}][stroke_width]`, l.width ?? 2);
        });

        // View State
        if (viewStateRef.current) {
            localStorage.setItem(`view-${pdfId}-${activeWorkspace.id}`, JSON.stringify(viewStateRef.current));
        }

        // Pages (Read directly from localStorage where InfiniteCanvas keeps them)
        try {
            const storedPagesStr = localStorage.getItem('airpdf_workspace_pages');
            if (storedPagesStr) {
                const pagesArr = JSON.parse(storedPagesStr);
                if (Array.isArray(pagesArr)) {
                    pagesArr.forEach((p, i) => {
                        fd.append(`pages[${i}][id]`, p.id || "");
                        fd.append(`pages[${i}][x]`, p.x || 0);
                        fd.append(`pages[${i}][y]`, p.y || 0);
                        fd.append(`pages[${i}][width]`, p.width || 1100);
                        fd.append(`pages[${i}][height]`, p.height || 1500);
                        fd.append(`pages[${i}][color]`, p.color || "#e8f3ff");
                    });
                }
            }
        } catch (e) {
            console.error("❌ Failed to parse pages for save", e);
        }

        // Groups
        (groups || []).forEach((g, i) => {
            fd.append(`groups[${i}][client_id]`, g.id);
            fd.append(`groups[${i}][name]`, g.name || "");
            fd.append(`groups[${i}][color]`, g.color || "#e0e7ff");
            fd.append(`groups[${i}][item_ids]`, JSON.stringify(g.itemIds || []));
            fd.append(`groups[${i}][collapsed]`, g.collapsed ? "true" : "false");
        });

        // Connections
        connections.forEach((c, i) => {
            const fromId = c.from ?? c.source_id;
            const toId = c.to ?? c.target_id;
            if (fromId != null && toId != null) {
                fd.append(`connections[${i}][id]`, c.id ?? "");
                fd.append(`connections[${i}][pdf_id]`, pdfId);
                fd.append(`connections[${i}][source_id]`, fromId);
                fd.append(`connections[${i}][target_id]`, toId);
            }
        });

        try {
            const resp = await api.saveWorkspaceData(pdfId, activeWorkspace.id, fd);
            const idMap = resp.data.id_map || {};

            // Update IDs
            setSnippets(prev => prev.map(s => ({ ...s, id: idMap[String(s.id)] || s.id })));
            setEditableBoxes(prev => prev.map(b => ({ ...b, id: idMap[String(b.id)] || b.id })));
            setConnections(prev => prev.map(c => ({ ...c, id: idMap[String(c.id)] || c.id, from: idMap[String(c.from)] || c.from, to: idMap[String(c.to)] || c.to })));
            setLines(prev => prev.map(l => ({ ...l, id: idMap[String(l.id)] || l.id })));
            setCrossPdfLinks(prev => prev.map(link => {
                const remapEndpoint = (endpoint) => {
                    if (!endpoint || endpoint.type !== "snippet") return endpoint;
                    return {
                        ...endpoint,
                        snippetId: idMap[String(endpoint.snippetId)] || endpoint.snippetId,
                    };
                };
                return {
                    ...link,
                    from: remapEndpoint(link.from),
                    to: remapEndpoint(link.to),
                };
            }));
        } catch (err) {
            console.error("❌ Save Workspace failed:", err);
        } finally {
            setSavingWorkspace(false);
            setIsDirty(false);
        }
    }, [
        pdfId, activeWorkspace, snippets, editableBoxes, lines, connections, groups,
        existingSnippetsMap, savingWorkspace, setSavingWorkspace, setSnippets,
        setEditableBoxes, setConnections, setLines, setIsDirty, viewStateRef,
        hydratedWorkspaceIdRef, crossPdfLinks, setCrossPdfLinks
    ]);

    // Autosave Timer logic here
    useEffect(() => {
        if (!enableAutosave || !pdfId || !activeWorkspace || autosaveInterval <= 0) return;
        const timer = setInterval(() => {
            if (isDirty && !savingWorkspace && !savingPdf) {
                savePdfChanges();
                saveWorkspaceChanges();
            }
        }, autosaveInterval);
        return () => clearInterval(timer);
    }, [autosaveInterval, pdfId, activeWorkspace, isDirty, savingWorkspace, savingPdf, savePdfChanges, saveWorkspaceChanges, enableAutosave]);

    // Save 3 seconds after the last change — catches fast tab-closes before the 30s timer fires
    useEffect(() => {
        if (!enableAutosave || !isDirty || !pdfId || !activeWorkspace) return;
        const timer = setTimeout(() => {
            if (!savingWorkspace && !savingPdf) {
                savePdfChanges();
                saveWorkspaceChanges();
            }
        }, 3000);
        return () => clearTimeout(timer);
    }, [enableAutosave, isDirty, pdfId, activeWorkspace, savingWorkspace, savingPdf, savePdfChanges, saveWorkspaceChanges]);

    const handleGlobalSave = useCallback(async () => {
        if (savingWorkspace || savingPdf) return;
        await Promise.all([savePdfChanges(), saveWorkspaceChanges()]);
    }, [savePdfChanges, saveWorkspaceChanges, savingWorkspace, savingPdf]);

    return { savePdfChanges, saveWorkspaceChanges, handleGlobalSave };
};
export default useWorkspaceSaver;
